import { WizardLimits, WizardPrize, WizardState } from '../wizardModel';
import { CampaignFormValues } from '../wizardSubmission';
import { Translate } from './ruleAdapter';

/** What an adapter needs beyond the answers: the copy, the tenant's timezone and the clock. */
export interface AdapterContext {
  t: Translate;
  timezone: string;
  now: () => string;
}

export const WHOLE = /^\d+$/;

export const positive = (value: string) => WHOLE.test(value) && Number(value) >= 1;

/** A checked limit line's number; an unchecked or empty one means no limit. */
export const limitValue = (on: boolean, value: string): number | null =>
  on && positive(value) ? Number(value) : null;

/** A prize as the module's prize editors write it (points: `IssuePointsModal`; a reward row). */
export const toOutcome = (prize: WizardPrize): Record<string, unknown> =>
  prize.type === 'points'
    ? {
        type: 'points',
        id: prize.loyalty?.id ?? null,
        name: prize.loyalty?.name ?? null,
        count: Number(prize.points),
        expiry_period: null,
        expiry_unit: null,
        expiry_fixed_date: null,
        expiry_use_campaign_end_date: false
      }
    : { type: 'reward', id: prize.reward?.id ?? null, name: prize.reward?.name ?? null };

export const prizeComplete = (prize: WizardPrize) =>
  prize.type === 'points'
    ? positive(prize.points) && prize.loyalty !== null
    : prize.reward !== null;

/** The fields every type shares: name, dates, timezone and enrolment. */
export const baseValues = (
  state: WizardState,
  type: CampaignFormValues['campaign_type'],
  context: AdapterContext,
  withSchedule: boolean
): CampaignFormValues => {
  const { limits } = state;
  return {
    campaign_type: type,
    name_en: state.name.trim(),
    start_date: withSchedule && limits.start !== null ? limits.start : context.now(),
    end_date: withSchedule && limits.endOn ? limits.end : null,
    timezone: context.timezone,
    auto_enrol_users: limits.autoEnrol,
    enrollable_until: !limits.autoEnrol && limits.enrolByOn ? limits.enrolBy : null
  };
};

/** "Complete within [n] days" on Quest and Progress, under self enrolment. */
export const durationValues = (limits: WizardLimits) =>
  !limits.autoEnrol && limits.windowOn
    ? { campaign_duration_mechanics: true, campaign_duration_days: Number(limits.windowDays) }
    : { campaign_duration_mechanics: false, campaign_duration_days: null };

/**
 * "N per period" as the module stores it: a `<scope>_interval_max_…` count every 1 period (the
 * frames draw no units input).
 */
export const intervalValues = (
  scope: 'account' | 'campaign',
  maxKey: string,
  on: boolean,
  value: string,
  period: string
): Record<string, unknown> =>
  on && positive(value)
    ? {
        [maxKey]: Number(value),
        [`${scope}_interval_units`]: 1,
        [`${scope}_interval_period`]: period
      }
    : { [maxKey]: null };
